/**
 * Login Page
 */

// Core imports
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// Style, SEO and settings
import Helmet from 'react-helmet';
import { Col, css, Grid, Row, up, styled, withTheme } from '@smooth-ui/core-sc';

// Additional Components/Containers
import Button from '_platform/src/components/Button/Button';
import LoadAsync from '_platform/src/utils/LoadAsync';
import PublicComponent from '_platform/src/utils/PublicComponent';
import getReturnUrlPath from '_platform/src/utils/getReturnUrlPath';

import { withSettings } from 'containers/WebApp/SettingsContext';
import logo from '../../theme/logo-goodwe-rewards.svg';

import BgImage from 'components/CustomComponents/Backgrounds/BgImage';

import FormStyles from 'components/FormStyles/FormStyles';

const LoginProvider = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginProvider" */ '_platform/src/containers/LoginProvider/LoginProvider'
  )
);
const LoginForm = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginForm" */ '_platform/src/components/LoginForm/LoginForm'
  )
);

const PageContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #6d6f72;

  .sui-row {
    margin-left: -20px;
    margin-right: -20px;
  }

  .sui-col {
    align-content: flex-end;
    padding-left: 20px;
    padding-right: 20px;
  }

  .sui-button,
  .register-button {
    border-radius: 24px;
    width: 100%;
    font-weight: bold;
    padding: 10px;
  }
`;

const LoginLogo = styled.h1`
  margin-bottom: 0;
  margin-top: 0;
  text-align: center;
  img {
    height: auto;
    width: 388px;
    max-width: 60%;

    ${up(
      'md',
      css`
        max-width: 90%;
      `
    )};
  }
`;

const LoginContainer = styled.div`
  .sui-label {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  small {
    font-size: 15px;
    display: block;
  }

  a {
    color: #6d6f72;
  }

  .reset-password {
    font-size: 15px;

    &:before {
      content: 'Forgot your password? ';
    }

    ${up(
      'lg',
      css`
        min-width: 300px;
      `
    )};
  }

  .login-form {
    .sui-input {
      background-color: white;
      border: 1px solid #dadada;
    }
  }
`;

const Card = styled.div`
  box-shadow: 0px 10px 15px #00000029;
  border-radius: 28px;
  padding: 40px 30px;
  background: white;
  max-width: 500px;
  margin: 10px auto;
`;

const LoginPage = ({ settings }) => (
  <PublicComponent
    redirectOnError={
      // Redirect to returnUrl, if it's not the same as loginPagePath
      (getReturnUrlPath !== settings.settingsApp.loginPagePath &&
        getReturnUrlPath) ||
      '/'
    }
  >
    <Helmet>
      <title>Login</title>
      {/*<meta name="description" content="Page description" />*/}
    </Helmet>
    <BgImage topOnly noPadding>
      <PageContainer className="page-container">
        <Grid>
          <Row>
            <Col xs={12} sm={12} lg={5} olg={2}>
              <Card>
                <LoginContainer>
                  <LoginLogo>
                    <img src={logo} alt="GoodWe Rewards" />
                  </LoginLogo>
                  <h2 className="text--primary text--center">
                    Welcome to GoodWe Rewards
                  </h2>
                  <p className="text--center">
                    Earn points for every eligible GoodWe model. Whether you're
                    new or returning, you're in the right place to claim your
                    rewards and browse the catalogue.
                  </p>
                  <FormStyles>
                    <LoginProvider disableMeta>
                      <LoginForm />
                    </LoginProvider>
                  </FormStyles>
                  <small className="spacer--top--small">
                    If you are having difficulties logging in, please contact us
                    via email to{' '}
                    <a href="mailto:hello@goodwerewards.com.au">
                      hello@goodwerewards.com.au
                    </a>{' '}
                    or speak to the rewards team on 1800 402 179.
                  </small>
                </LoginContainer>
              </Card>
            </Col>
            <Col xs={12} md={12} lg={3}>
              <Card>
                <h2 className="text--primary text--center">
                  New to GoodWe Rewards?
                </h2>
                <p className="text--center">
                  Start by creating your account to submit claims and begin
                  earning points.
                </p>
                <p className="text--center">
                  Click the Register button to get started.
                </p>
                <Button as={Link} className="register-button" to="/register/">
                  Register
                </Button>
              </Card>
            </Col>
          </Row>
        </Grid>
      </PageContainer>
    </BgImage>
  </PublicComponent>
);

LoginPage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withTheme(withSettings(LoginPage));
